import { premiumBuildingsPrices } from "@/config/constants";
import {
	isChristmasMountainLumberjackActive,
	isHuntingMountaineeringActive,
} from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import useTileRepository from "@/hooks/useTileRepository";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import {
	type ProductionValue,
	type SuppliesValue,
	TileAction,
	type TileConfig,
	type TileConfigItem,
	type UpgradeParams,
} from "@/types/tile";
import { ceilNumber } from "@/utils/number";
import { FrameName } from ".";
import { UpgradeIds, upgrades } from "./upgrades";

export const CUTTABLE_LEVEL = 4;

/** CONFIG **/

const config: TileConfig = {
	[FrameName.DEEP_SEA_BIG_FISH]: {
		inSearchTool: true,
		exploreCoefficient: 1.7,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.PRODUCTION_RESOURCE_UNIT,
				(): number => {
					const base = 10;
					let multiplier = 1;
					if (
						useUpgradesStore().getCurrentLevelById(
							UpgradeIds.harpoon,
						) > 0
					) {
						// Nemozem importovat utilku, lebo by vznikla circular dependency, preto tu duplikujem kod z getUpgradeCurrentReplaceValueById
						const upgrade = upgrades.get(UpgradeIds.harpoon);
						if (!upgrade) return 0;
						multiplier =
							1 +
							(upgrade.replaceValue || 0) *
								useUpgradesStore().getCurrentLevelById(
									UpgradeIds.harpoon,
								);
					}

					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.DEEP_SEA_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 3_500,
					},
				},
			],
			[
				FrameName.DEEP_SEA_BIG_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.GOODS]: 50_000,
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
		]),
	},
	[FrameName.DEEP_SEA_MEDIUM_FISH]: {
		inSearchTool: true,
		exploreCoefficient: 1.7,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 6],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.DEEP_SEA_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 3_500,
					},
				},
			],
			[
				FrameName.DEEP_SEA_BIG_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.GOODS]: 50_000,
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
		]),
	},
	[FrameName.DEEP_SEA]: {
		exploreCoefficient: 1.7,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.DEEP_SEA_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 3_500,
					},
				},
			],
			[
				FrameName.DEEP_SEA_BIG_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.GOODS]: 50_000,
					},
				},
			],
		]),
	},
	[FrameName.DENSE_FOREST]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 8]]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 2],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.GRASSLAND,
				{
					price: {
						[ResourceType.COINS]: 8_000,
					},
					reward: {
						[ResourceType.LUMBER]: 200,
					},
					text: "button.cut",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 8_300,
					},
					reward: {
						[ResourceType.LUMBER]: 200,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 9_000,
						[ResourceType.STONE]: 50,
					},
					reward: {
						[ResourceType.LUMBER]: 200,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 13_000,
						[ResourceType.STONE]: 500,
					},
					reward: {
						[ResourceType.LUMBER]: 200,
					},
				},
			],
			[
				FrameName.HUNTING_CAMP,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.RANGERS,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.RANGERS],
					},
				},
			],
		]),
		action: TileAction.CUT,
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
			FrameName.HUNTING_CAMP,
		]),
	},
	[FrameName.FOREST]: {
		exploreCoefficient: 1.3,
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 4]]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.GRASSLAND,
				{
					price: {
						[ResourceType.COINS]: 2_500,
					},
					reward: {
						[ResourceType.LUMBER]: 100,
					},
					text: "button.cut",
				},
			],
			[
				FrameName.DENSE_FOREST,
				{
					price: {
						[ResourceType.COINS]: 4_000,
					},
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 2_800,
					},
					reward: {
						[ResourceType.LUMBER]: 100,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 3_500,
						[ResourceType.STONE]: 50,
					},
					reward: {
						[ResourceType.LUMBER]: 100,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 7_500,
						[ResourceType.STONE]: 500,
					},
					reward: {
						[ResourceType.LUMBER]: 100,
					},
				},
			],
			[
				FrameName.HUNTING_CAMP,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.RANGERS,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.RANGERS],
					},
				},
			],
		]),
		action: TileAction.CUT,
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
			FrameName.HUNTING_CAMP,
		]),
	},
	[FrameName.GEMS_VEIN]: {
		inSearchTool: true,
		exploreCoefficient: 4,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.GEMS_MINE,
				{
					price: {
						[ResourceType.COINS]: 50_000_000,
					},
				},
			],
		]),
	},
	[FrameName.GOLD_VEIN]: {
		inSearchTool: true,
		exploreCoefficient: 4,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.GOLD_MINE,
				{
					price: {
						[ResourceType.COINS]: 5_000,
					},
				},
			],
			[
				FrameName.PREMIUM_GOLD_MINE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.PREMIUM_GOLD_MINE],
					},
				},
			],
		]),
	},
	[FrameName.GRASSLAND]: {
		// TODO: isInternal: true,
		exploreCoefficient: 1,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.GATHERERS_HUT,
				{
					price: {
						[ResourceType.COINS]: 600,
					},
				},
			],
			[
				FrameName.FOREST,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
				},
			],
			[
				FrameName.DENSE_FOREST,
				{
					price: {
						[ResourceType.COINS]: 5_000,
					},
				},
			],
			[
				FrameName.LUMBER_MILL,
				{
					price: {
						[ResourceType.COINS]: 1_500,
					},
				},
			],
			[
				FrameName.WHEAT_FARM,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 1_000,
					},
				},
			],
			[
				FrameName.INDUSTRIAL_LUMBER_MILL,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.INDUSTRIAL_LUMBER_MILL
							],
					},
				},
			],
			[
				FrameName.SMALL_STOCKPILE,
				{
					price: {
						[ResourceType.COINS]: 50_000,
						[ResourceType.LUMBER]: 7_500,
						[ResourceType.STONE]: 5_000,
						[ResourceType.CLAY]: 1_500,
					},
				},
			],
			[
				FrameName.FURNACE,
				{
					price: {
						[ResourceType.COINS]: 150_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 10_000,
						[ResourceType.CLAY]: 2_500,
					},
				},
			],
			[
				FrameName.MILL,
				{
					price: {
						[ResourceType.COINS]: 100_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 5_000,
						[ResourceType.GOODS]: 500,
					},
				},
			],
			[
				FrameName.OXEN_STABLE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.OXEN_STABLE],
					},
				},
			],
			[
				FrameName.FOUNDRY,
				{
					price: {
						[ResourceType.COINS]: 100_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 20_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 5_000,
						[ResourceType.GOODS]: 2_000,
					},
				},
			],
			[
				FrameName.WAREHOUSE,
				{
					price: {
						[ResourceType.COINS]: 150_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 10_000,
						[ResourceType.CLAY]: 5_000,
						[ResourceType.IRON]: 2_500,
					},
				},
			],
			[
				FrameName.UNIVERSAL_WAREHOUSE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.UNIVERSAL_WAREHOUSE
							],
					},
				},
			],
			[
				FrameName.OUTPOST,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.STONE]: 50_000,
						[ResourceType.CLAY]: 35_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.STEEL]: 15_000,
						[ResourceType.ARMY]: 250,
					},
				},
			],
			[
				FrameName.HUNTERS_LODGE,
				{
					price: {
						[ResourceType.COINS]: 50_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.IRON]: 2_500,
						[ResourceType.GOODS]: 1_500,
					},
				},
			],
			[
				FrameName.TANNERY,
				{
					price: {
						[ResourceType.COINS]: 30_000,
						[ResourceType.LUMBER]: 10_000,
						[ResourceType.STONE]: 5_000,
						[ResourceType.CLAY]: 3_000,
					},
				},
			],
			[
				FrameName.LIVESTOCK_FARM,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 10_000,
						[ResourceType.CLAY]: 5_000,
						[ResourceType.IRON]: 2_500,
						[ResourceType.GOODS]: 1_500,
					},
				},
			],
			[
				FrameName.BREWERY,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 100_000,
						[ResourceType.GOODS]: 25_000,
					},
				},
			],
			[
				FrameName.INDUSTRIAL_FOUNDRY,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.INDUSTRIAL_FOUNDRY
							],
					},
				},
			],
			[
				FrameName.BRICKYARD,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.BRICKYARD],
					},
				},
			],
			[
				FrameName.GOLD_PROSPECTOR,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.IRON]: 150_000,
						[ResourceType.GOODS]: 25_000,
						[ResourceType.STEEL]: 75_000,
					},
				},
			],
			[
				FrameName.APIARY,
				{
					price: {
						[ResourceType.COINS]: 5_000_000,
						[ResourceType.LUMBER]: 500_000,
						[ResourceType.STONE]: 250_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.GOODS]: 150_000,
					},
				},
			],
			[
				FrameName.CANDLEMAKER,
				{
					price: {
						[ResourceType.COINS]: 7_500_000,
						[ResourceType.LUMBER]: 750_000,
						[ResourceType.STONE]: 400_000,
						[ResourceType.CLAY]: 200_000,
						[ResourceType.GOODS]: 200_000,
						[ResourceType.STEEL]: 75_000,
					},
				},
			],
			[
				FrameName.COUNTRY_HOUSE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.PUMPKIN_FARM,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.LIGHTHOUSE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.ROOF_TILE_FACTORY,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.TURKEY_FARM,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.MINT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.BANQUET,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.MINSTRELS_PODIUM,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.MONOLITH_CIRCLE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.LUCKY_CAULDRON,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.MILITARY_ACADEMY,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.MILITARY_ACADEMY],
					},
				},
			],
			[
				FrameName.MILITIA,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.ARMY]: 500,
						[ResourceType.RATIONS]: 250,
					},
				},
			],
			[
				FrameName.SUPPLY_CART,
				{
					price: {
						[ResourceType.COINS]: 2_500_000,
						[ResourceType.ARMY]: 1_000,
						[ResourceType.RATIONS]: 1_000,
					},
				},
			],
			[
				FrameName.SUPPLY_CARAVAN,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.SUPPLY_CARAVAN],
					},
				},
			],
			[
				FrameName.ARCHERS,
				{
					price: {
						[ResourceType.COINS]: 1_500_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.GOODS]: 50_000,
						[ResourceType.ARMY]: 750,
						[ResourceType.RATIONS]: 350,
					},
				},
			],
			[
				FrameName.LIGHT_CAVALRY,
				{
					price: {
						[ResourceType.COINS]: 3_000_000,
						[ResourceType.IRON]: 100_000,
						[ResourceType.GOODS]: 25_000,
						[ResourceType.STEEL]: 100_000,
						[ResourceType.ARMY]: 1_500,
						[ResourceType.RATIONS]: 500,
					},
				},
			],
			[
				FrameName.COMMAND_POST,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.COMMAND_POST],
					},
				},
			],
			[
				FrameName.DARKWOOD_LUMBER_MILL,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.TOYMAKERS_WORKSHOP,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.FORTRESS,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.RABBIT_FARM,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 300,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 1_000,
						[ResourceType.STONE]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.STONE]: 500,
					},
				},
			],
			[
				FrameName.STORAGE_OVERSEER,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.PEA_PATCH,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.MANOR,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.LARGE_BREWERY,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.SMALL_HOUSES,
				{
					price: {
						[ResourceType.COINS]: 2_000,
					},
				},
			],
			[
				FrameName.FIRE_FESTIVAL_PYRE,
				{
					price: {
						[ResourceType.EVENTIUM]: (): number => {
							const inStorage =
								useBuildingsStorageStore()[
									FrameName.FIRE_FESTIVAL_PYRE
								] || 0;
							const built =
								useTileRepository().getTileCount2(
									new Set<FrameName>([
										FrameName.FIRE_FESTIVAL_PYRE,
									]),
								) || 0;

							switch (built + inStorage) {
								case 0:
									return 0;
								case 1:
									return 1_000;
								case 2:
									return 1_350;
								case 3:
									return 1_823;
								case 4:
									return 2_460;
								case 5:
									return 3_322;
								case 6:
									return 4_484;
								case 7:
									return 6_053;
								case 8:
									return 8_172;
								case 9:
									return 11_032;
								case 10:
									return 14_894;
								case 11:
									return 20_107;
								case 12:
									return 27_144;
								case 13:
									return 36_644;
								case 14:
									return 49_470;
								case 15:
									return 66_784;
								default:
									return 999_999_999;
							}
						},
					},
				},
			],
			[
				FrameName.BOUNTIFUL_MARKETPLACE,
				{
					price: {
						[ResourceType.EVENTIUM]: (): number => {
							const inStorage =
								useBuildingsStorageStore()[
									FrameName.BOUNTIFUL_MARKETPLACE
								] || 0;
							const built =
								useTileRepository().getTileCount2(
									new Set<FrameName>([
										FrameName.BOUNTIFUL_MARKETPLACE,
									]),
								) || 0;

							switch (built + inStorage) {
								case 0:
								case 1:
									return 0;
								case 2:
									return 1_500;
								case 3:
									return 5_000;
								case 4:
									return 20_000;
								case 5:
									return 40_000;
								case 6:
									return 70_000;
								case 7:
									return 100_000;
								case 8:
									return 150_000;
								case 9:
									return 200_000;
								case 10:
									return 300_000;
								case 11:
									return 400_000;
								case 12:
									return 500_000;
								case 13:
									return 600_000;
								case 14:
									return 700_000;
								case 15:
									return 800_000;
								case 16:
									return 1_000_000;
								case 17:
									return 1_200_000;
								case 18:
									return 1_500_000;
								case 19:
									return 2_000_000;
								default:
									return 999_999_999;
							}
						},
					},
				},
			],
			[
				FrameName.SOLSTICE_WINDMILL,
				{
					// Budova ide zo storage.
				},
			],
		]),
	},
	[FrameName.RUINED_FOREST]: {
		exploreCoefficient: 1.2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.GRASSLAND,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
					reward: {
						[ResourceType.LUMBER]: 50,
					},
					text: "button.remove",
				},
			],
			[
				FrameName.FOREST,
				{
					price: {
						[ResourceType.COINS]: 2_000,
					},
					text: "button.build",
				},
			],
			[
				FrameName.DENSE_FOREST,
				{
					price: {
						[ResourceType.COINS]: 6_000,
					},
					text: "button.build",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 1_300,
					},
					reward: {
						[ResourceType.LUMBER]: 50,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 2_000,
						[ResourceType.STONE]: 50,
					},
					reward: {
						[ResourceType.LUMBER]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 6_000,
						[ResourceType.STONE]: 500,
					},
					reward: {
						[ResourceType.LUMBER]: 50,
					},
				},
			],
		]),
		action: TileAction.REMOVE,
	},
	[FrameName.HILLS]: {
		exploreCoefficient: 1.8,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.PRODUCTION_RESOURCE_UNIT,
				(): number => {
					let production: number = 0;

					if (isChristmasMountainLumberjackActive())
						production =
							(getTerrainConfig(
								FrameName.OLD_FOREST,
							).production.get(
								ResourceType.PRODUCTION_RESOURCE_UNIT,
							)! as number) ?? 0;

					if (
						useUpgradesStore().getCurrentLevelById(
							UpgradeIds.mountain_lumberjacks,
						) > 0
					)
						production =
							(getTerrainConfig(
								FrameName.OLD_FOREST,
							).production.get(
								ResourceType.PRODUCTION_RESOURCE_UNIT,
							)! as number) ?? 0;

					return production;
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.STONE_QUARRY,
				{
					price: {
						[ResourceType.COINS]: 10_000,
						[ResourceType.LUMBER]: 2_500,
					},
				},
			],
			[
				FrameName.LARGE_STONE_QUARRY,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.LARGE_STONE_QUARRY
							],
					},
				},
			],
			[
				FrameName.IRON_MINE,
				{
					price: {
						[ResourceType.COINS]: 25_000,
						[ResourceType.LUMBER]: 5_000,
						[ResourceType.STONE]: 2_500,
						[ResourceType.CLAY]: 500,
					},
				},
			],
			[
				FrameName.ADVANCED_IRON_MINE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.ADVANCED_IRON_MINE
							],
					},
				},
			],
			[
				FrameName.HILLS_SCOUT_TOWER,
				{
					price: {
						[ResourceType.COINS]: 150_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 50_000,
						[ResourceType.ARMY]: 100,
					},
				},
			],
			[
				FrameName.HILLY_ROAD,
				{
					price: {
						[ResourceType.COINS]: 50_000,
						[ResourceType.LUMBER]: 10_000,
						[ResourceType.IRON]: 1_000,
					},
				},
			],
			[
				FrameName.HILLS_BLAST_FURNACE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.HILLS_LOOKOUT_TOWER,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.HIGHLAND_HUNTER,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.SHEPHERDS_LODGE,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<SuppliesValue>([
			(): FrameName | null => {
				if (isChristmasMountainLumberjackActive())
					return FrameName.DARKWOOD_LUMBER_MILL;
				return null;
			},
			(): Array<FrameName> | null => {
				if (
					useUpgradesStore().getCurrentLevelById(
						UpgradeIds.mountain_lumberjacks,
					) > 0
				)
					return [
						FrameName.LUMBER_MILL,
						FrameName.INDUSTRIAL_LUMBER_MILL,
						FrameName.WATER_SAW_MILL,
					];
				return null;
			},
			FrameName.HIGHLAND_HUNTER,
		]),
	},
	[FrameName.MOUNTAINS]: {
		exploreCoefficient: 2.2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.PRODUCTION_RESOURCE_UNIT,
				(): number => {
					let production: number = 0;

					if (isChristmasMountainLumberjackActive())
						production +=
							(getTerrainConfig(
								FrameName.OLD_FOREST,
							).production.get(
								ResourceType.PRODUCTION_RESOURCE_UNIT,
							)! as number) ?? 0;

					if (isHuntingMountaineeringActive())
						production +=
							(getTerrainConfig(FrameName.HILLS).production.get(
								ResourceType.PRODUCTION_RESOURCE_UNIT,
							)! as number) ?? 0;

					return production;
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.STONE_QUARRY,
				{
					price: {
						[ResourceType.COINS]: 10_000,
						[ResourceType.LUMBER]: 2_500,
					},
				},
			],
			[
				FrameName.LARGE_STONE_QUARRY,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.LARGE_STONE_QUARRY
							],
					},
				},
			],
			[
				FrameName.IRON_MINE,
				{
					price: {
						[ResourceType.COINS]: 25_000,
						[ResourceType.LUMBER]: 5_000,
						[ResourceType.STONE]: 2_500,
						[ResourceType.CLAY]: 500,
					},
				},
			],
			[
				FrameName.ADVANCED_IRON_MINE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.ADVANCED_IRON_MINE
							],
					},
				},
			],
			[
				FrameName.MOUNTAINS_SCOUT_TOWER,
				{
					price: {
						[ResourceType.COINS]: 150_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 50_000,
						[ResourceType.ARMY]: 100,
					},
				},
			],
			[
				FrameName.MOUNTAIN_ROAD,
				{
					price: {
						[ResourceType.COINS]: 50_000,
						[ResourceType.LUMBER]: 10_000,
						[ResourceType.IRON]: 1_000,
					},
				},
			],
			[
				FrameName.SILVER_MINE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.MOUNTAINS_BLAST_FURNACE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.MOUNTAINS_LOOKOUT_TOWER,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<SuppliesValue>([
			FrameName.DARKWOOD_LUMBER_MILL,
			(): FrameName | null => {
				if (isHuntingMountaineeringActive())
					return FrameName.HIGHLAND_HUNTER;
				return null;
			},
		]),
	},
	[FrameName.OLD_FOREST]: {
		inSearchTool: true,
		exploreCoefficient: 2.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 5],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.GRASSLAND,
				{
					price: {
						[ResourceType.COINS]: 25_000,
					},
					reward: {
						[ResourceType.LUMBER]: 2_500,
					},
					text: "button.cut",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 25_300,
					},
					reward: {
						[ResourceType.LUMBER]: 2_500,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 26_000,
						[ResourceType.STONE]: 50,
					},
					reward: {
						[ResourceType.LUMBER]: 2_500,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 30_000,
						[ResourceType.STONE]: 500,
					},
					reward: {
						[ResourceType.LUMBER]: 2_500,
					},
				},
			],
			[
				FrameName.HUNTING_CAMP,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.RANGERS,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.RANGERS],
					},
				},
			],
			[
				FrameName.HAUNTED_MANSION,
				{
					// Budova ide zo storage.
				},
			],
		]),
		action: TileAction.CUT,
		supplies: new Set<FrameName>([
			FrameName.LUMBER_MILL,
			FrameName.INDUSTRIAL_LUMBER_MILL,
			FrameName.WATER_SAW_MILL,
			FrameName.HUNTERS_LODGE,
			FrameName.DARKWOOD_LUMBER_MILL,
			FrameName.HUNTING_CAMP,
			FrameName.HAUNTED_MANSION,
		]),
	},
	[FrameName.RIVER3]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER5]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.FORD_CROSSING,
				{
					price: {
						[ResourceType.COINS]: 500,
					},
				},
			],
			[
				FrameName.WOODEN_BRIDGE,
				{
					price: {
						[ResourceType.COINS]: 2_000,
						[ResourceType.LUMBER]: 500,
					},
				},
			],
			[
				FrameName.STONE_BRIDGE,
				{
					price: {
						[ResourceType.COINS]: 10_000,
						[ResourceType.STONE]: 2_500,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER6]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER7]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER9]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER10]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.FORD_CROSSING,
				{
					price: {
						[ResourceType.COINS]: 500,
					},
				},
			],
			[
				FrameName.WOODEN_BRIDGE,
				{
					price: {
						[ResourceType.COINS]: 2_000,
						[ResourceType.LUMBER]: 500,
					},
				},
			],
			[
				FrameName.STONE_BRIDGE,
				{
					price: {
						[ResourceType.COINS]: 10_000,
						[ResourceType.STONE]: 2_500,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER11]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER12]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER13]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER14]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER15]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.FISHERMANS_HUT,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.WATER_SAW_MILL,
				{
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 25_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 15_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.WATER_MILL,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
						[ResourceType.GOODS]: 5_000,
					},
				},
			],
			[
				FrameName.BOTTLETRAP,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BOTTLETRAP]),
	},
	[FrameName.RIVER_DELTA1]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.RIVER_DELTA2]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.RIVER_DELTA4]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.RIVER_DELTA8]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.RIVER_SPRING1]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.RIVER_SPRING2]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.RIVER_SPRING4]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.RIVER_SPRING8]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.SETTLEMENT_EMPTY]: {
		exploreCoefficient: 0, // je zadarmo
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SMALL_HOUSES,
				{
					price: {
						[ResourceType.COINS]: 2_000,
					},
				},
			],
			[
				FrameName.BAKERY,
				{
					price: {
						[ResourceType.COINS]: 15_000,
						[ResourceType.LUMBER]: 2_500,
						[ResourceType.STONE]: 1_000,
					},
				},
			],
			[
				FrameName.TOLL_HOUSE,
				{
					price: {
						[ResourceType.COINS]: 50_000,
						[ResourceType.LUMBER]: 15_000,
						[ResourceType.STONE]: 7_500,
					},
				},
			],
			[
				FrameName.BLACKSMITH,
				{
					price: {
						[ResourceType.COINS]: 150_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 20_000,
						[ResourceType.CLAY]: 10_000,
						[ResourceType.IRON]: 7_500,
					},
				},
			],
			[
				FrameName.MARKET,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 75_000,
						[ResourceType.STONE]: 50_000,
						[ResourceType.CLAY]: 20_000,
						[ResourceType.IRON]: 10_000,
						[ResourceType.GOODS]: 1_000,
					},
				},
			],
			[
				FrameName.TOOLMAKER,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 15_000,
						[ResourceType.IRON]: 10_000,
						[ResourceType.GOODS]: 2_500,
						[ResourceType.STEEL]: 1_000,
					},
				},
			],
			[
				FrameName.ARMORY,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 35_000,
						[ResourceType.IRON]: 15_000,
						[ResourceType.STEEL]: 10_000,
					},
				},
			],
			[
				FrameName.BARRACKS,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 75_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.STEEL]: 15_000,
					},
				},
			],
			[
				FrameName.HEALERS_HUT,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 15_000,
						[ResourceType.GOODS]: 10_000,
					},
				},
			],
			[
				FrameName.TAILORS_WORKSHOP,
				{
					price: {
						[ResourceType.COINS]: 150_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 15_000,
						[ResourceType.IRON]: 5_000,
						[ResourceType.GOODS]: 2_500,
					},
				},
			],
			[
				FrameName.MEDIUM_HOUSES,
				{
					price: {
						[ResourceType.COINS]: 35_000,
						[ResourceType.LUMBER]: 10_000,
						[ResourceType.STONE]: 5_000,
						[ResourceType.CLAY]: 1_500,
					},
				},
			],
			[
				FrameName.UNIVERSITY,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 75_000,
						[ResourceType.CLAY]: 50_000,
						[ResourceType.IRON]: 30_000,
						[ResourceType.GOODS]: 20_000,
						[ResourceType.STEEL]: 10_000,
					},
				},
			],
			[
				FrameName.INN,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 75_000,
						[ResourceType.GOODS]: 35_000,
					},
				},
			],
			[
				FrameName.SHOEMAKER,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.STONE]: 75_000,
						[ResourceType.CLAY]: 50_000,
						[ResourceType.IRON]: 20_000,
						[ResourceType.STEEL]: 5_000,
					},
				},
			],
			[
				FrameName.PATISSERIE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.PATISSERIE],
					},
				},
			],
			[
				FrameName.GUARD_TOWER,
				{
					price: {
						[ResourceType.COINS]: 1_500_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.STONE]: 250_000,
						[ResourceType.IRON]: 50_000,
						[ResourceType.STEEL]: 25_000,
						[ResourceType.ARMY]: 1_500,
					},
				},
			],
			[
				FrameName.CARPENTRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.CARPENTRY_DISTRICT
							],
					},
				},
			],
			[
				FrameName.STONEMASONRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.STONEMASONRY_DISTRICT
							],
					},
				},
			],
			[
				FrameName.TAX_OFFICE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.TAX_OFFICE],
					},
				},
			],
			[
				FrameName.LARGE_HOUSES,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 50_000,
						[ResourceType.IRON]: 50_000,
						[ResourceType.STEEL]: 20_000,
					},
				},
			],
			[
				FrameName.ARTISAN_SMITH,
				{
					price: {
						[ResourceType.COINS]: 250_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.STONE]: 75_000,
						[ResourceType.IRON]: 20_000,
						[ResourceType.GOODS]: 10_000,
						[ResourceType.STEEL]: 5_000,
					},
				},
			],
			[
				FrameName.SCULPTOR,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.ARENA,
				{
					price: {
						[ResourceType.COINS]: 2_500_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.STONE]: 150_000,
						[ResourceType.IRON]: 60_000,
						[ResourceType.GOODS]: 20_000,
						[ResourceType.STEEL]: 40_000,
						[ResourceType.ARMY]: 250,
					},
				},
			],
			[
				FrameName.FLETCHER,
				{
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 75_000,
						[ResourceType.IRON]: 50_000,
						[ResourceType.STEEL]: 25_000,
					},
				},
			],
			[
				FrameName.ARCHERY_RANGE,
				{
					price: {
						[ResourceType.COINS]: 1_500_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.STONE]: 200_000,
						[ResourceType.IRON]: 100_000,
						[ResourceType.STEEL]: 50_000,
						[ResourceType.ARMY]: 2_500,
					},
				},
			],
			[
				FrameName.BATH_HOUSE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.BATH_HOUSE],
					},
				},
			],
			[
				FrameName.SADDLER,
				{
					price: {
						[ResourceType.COINS]: 5_000_000,
						[ResourceType.LUMBER]: 1_000_000,
						[ResourceType.STONE]: 750_000,
						[ResourceType.CLAY]: 500_000,
						[ResourceType.IRON]: 250_000,
						[ResourceType.GOODS]: 250_000,
						[ResourceType.STEEL]: 100_000,
					},
				},
			],
			[
				FrameName.STABLES,
				{
					price: {
						[ResourceType.COINS]: 15_000_000,
						[ResourceType.LUMBER]: 2_000_000,
						[ResourceType.STONE]: 1_000_000,
						[ResourceType.CLAY]: 750_000,
						[ResourceType.IRON]: 750_000,
						[ResourceType.GOODS]: 300_000,
						[ResourceType.STEEL]: 200_000,
						[ResourceType.ARMY]: 10_000,
					},
				},
			],
			[
				FrameName.TEMPLE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.TEMPLE],
					},
				},
			],
			[
				FrameName.APOTHECARY,
				{
					price: {
						[ResourceType.COINS]: 10_000_000,
						[ResourceType.LUMBER]: 1_000_000,
						[ResourceType.STONE]: 750_000,
						[ResourceType.CLAY]: 250_000,
						[ResourceType.IRON]: 100_000,
						[ResourceType.GOODS]: 500_000,
					},
				},
			],
			[
				FrameName.ARMAMENTS_TRADER,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.ENKINDLED_HOUSEHOLD,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.RATIONS_SUPPLIER,
				{
					price: {
						[ResourceType.COINS]: 5_000_000,
						[ResourceType.LUMBER]: 500_000,
						[ResourceType.STONE]: 300_000,
						[ResourceType.IRON]: 150_000,
						[ResourceType.GOODS]: 200_000,
						[ResourceType.STEEL]: 100_000,
						[ResourceType.ARMY]: 1_500,
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.CANDLEMAKER]),
	},
	[FrameName.SHALLOW_SEA_MEDIUM_FISH]: {
		inSearchTool: true,
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 6],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SHALLOW_SEA_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 3_500,
					},
				},
			],
			[
				FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
				{
					price: {
						[ResourceType.COINS]: 15_000,
						[ResourceType.LUMBER]: 5_000,
					},
				},
			],
			[
				FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.GOODS]: 50_000,
					},
				},
			],
			[
				FrameName.RAFT,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			// TODO: trap tu nema byt a ani v inych, treba pretriedit kazde 1 pouzitie
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
		]),
	},
	[FrameName.SHALLOW_SEA_ROCKS]: {
		exploreCoefficient: 2,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.CORSAIR_SHIP,
				{
					// Budova ide zo storage.
				},
			],
		]),
	},
	[FrameName.SHALLOW_SEA_SMALL_FISH]: {
		inSearchTool: true,
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 2],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SHALLOW_SEA_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 3_500,
					},
				},
			],
			[
				FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
				{
					price: {
						[ResourceType.COINS]: 15_000,
						[ResourceType.LUMBER]: 5_000,
					},
				},
			],
			[
				FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.GOODS]: 50_000,
					},
				},
			],
			[
				FrameName.RAFT,
				{
					// Budova ide zo storage.
				},
			],
		]),
		supplies: new Set<FrameName>([
			FrameName.SHALLOW_SEA_FISHING_BOAT,
			FrameName.DEEP_SEA_FISHING_BOAT,
			FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			FrameName.DEEP_SEA_BIG_FISHING_BOAT,
			FrameName.RAFT,
		]),
	},
	[FrameName.SHALLOW_SEA]: {
		exploreCoefficient: 1.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SHIPYARD,
				{
					price: {
						[ResourceType.COINS]: 20_000,
						[ResourceType.LUMBER]: 10_000,
						[ResourceType.STONE]: 2_500,
					},
				},
			],
			[
				FrameName.SHALLOW_SEA_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 3_500,
					},
				},
			],
			[
				FrameName.PORT,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.STONE]: 75_000,
						[ResourceType.CLAY]: 50_000,
						[ResourceType.IRON]: 15_000,
						[ResourceType.GOODS]: 10_000,
						[ResourceType.STEEL]: 7_500,
						[ResourceType.ARMY]: 250,
					},
				},
			],
			[
				FrameName.BIG_SHIPYARD,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.FISHING_VILLAGE,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.SEAWEED_FARM,
				{
					// Budova ide zo storage.
				},
			],
			[
				FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
				{
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.GOODS]: 50_000,
					},
				},
			],
			[
				FrameName.RAFT,
				{
					// Budova ide zo storage.
				},
			],
		]),
	},
	[FrameName.SWAMP]: {
		inSearchTool: true,
		exploreCoefficient: 2.5,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.CLAY_PIT,
				{
					price: {
						[ResourceType.COINS]: 25_000,
						[ResourceType.LUMBER]: 7_500,
						[ResourceType.STONE]: 2_000,
					},
				},
			],
			[
				FrameName.LARGE_CLAY_PIT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.LARGE_CLAY_PIT],
					},
				},
			],
		]),
		action: TileAction.REMOVE,
	},
	[FrameName.IRON_MOUNTAINS]: {
		inSearchTool: true,
		exploreCoefficient: 3,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.MOUNTAIN_BELLOWS,
				{
					price: {
						[ResourceType.COINS]: 10_000_000,
						[ResourceType.LUMBER]: 500_000,
						[ResourceType.STONE]: 300_000,
						[ResourceType.CLAY]: 50_000,
						[ResourceType.IRON]: 100_000,
						[ResourceType.GOODS]: 50_000,
						[ResourceType.STEEL]: 100_000,
					},
				},
			],
		]),
	},
	[FrameName.SHALLOW_SEA_CORAL_REEF]: {
		inSearchTool: true,
		exploreCoefficient: 3,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.25],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SHALLOW_SEA_PEARL_HUNTER,
				{
					price: {
						[ResourceType.COINS]: 750_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.GOODS]: 25_000,
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.RAFT]),
	},
	[FrameName.DEEP_SEA_CORAL_REEF]: {
		inSearchTool: true,
		exploreCoefficient: 3,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.25],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SHALLOW_SEA_PEARL_HUNTER,
				{
					price: {
						[ResourceType.COINS]: 750_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.GOODS]: 25_000,
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.RAFT]),
	},
	[FrameName.COAL_DEPOSIT]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
	[FrameName.SALT_DEPOSIT]: {
		inSearchTool: true,
		exploreCoefficient: 1,
		requirements: new Map<ResourceType, number>(),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
	},
};

config[FrameName.SETTLEMENT_EMPTY1] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY2] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY3] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY4] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY5] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY6] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY7] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY8] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY9] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY10] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY11] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY12] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY13] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY14] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY15] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY16] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY17] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY18] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.SETTLEMENT_EMPTY19] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_EMPTY],
);
config[FrameName.GRASSLAND1] = Object.assign({}, config[FrameName.GRASSLAND]);
config[FrameName.GRASSLAND2] = Object.assign({}, config[FrameName.GRASSLAND]);
config[FrameName.GRASSLAND3] = Object.assign({}, config[FrameName.GRASSLAND]);
config[FrameName.GRASSLAND4] = Object.assign({}, config[FrameName.GRASSLAND]);

export const getTerrainConfig = (frameName: FrameName): TileConfigItem => {
	const terrainConfig = config?.[frameName];
	if (!terrainConfig)
		throw new Error(`Terrain config not found for type: ${frameName}!`);

	return terrainConfig;
};

export const isTerrain = (frameName: FrameName): boolean => frameName in config;

export enum TileVisibilityType {
	BLACK = 0,
	FOG = 1, // TODO: rename to FOW
	EXPLORED = 2,
}

export const ALL_VISIBILITY_TYPES = new Set([
	TileVisibilityType.BLACK,
	TileVisibilityType.FOG,
	TileVisibilityType.EXPLORED,
]);

export const getNewTerrainsFromLevel = (level: number): Array<FrameName> => {
	const newTerrains: Array<FrameName> = [];
	for (const [frameName, terrainConfig] of Object.entries(config)) {
		if (
			terrainConfig.requirements.has(ResourceType.LEVEL) &&
			terrainConfig.requirements.get(ResourceType.LEVEL)! === level
		) {
			newTerrains.push(frameName as FrameName);
		}
	}
	return newTerrains;
};

export const getAllTerrainsForSearchTool = (): Array<FrameName> => {
	const terrains: Array<FrameName> = [];
	for (const [frameName, terrainConfig] of Object.entries(config)) {
		if (terrainConfig.inSearchTool) {
			terrains.push(frameName as FrameName);
		}
	}
	return terrains;
};
